import { Injectable, computed, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

export interface Miga {
  texto: string;
  /** Enlace; null en la página actual. */
  url: string | null;
}

/** Ruta jerárquica (migas de pan) a partir de la URL. Las etiquetas de entidades las aportan las fichas cuando cargan. */
export function construirMigas(url: string, etiquetas: Record<string, string>, extra: string | null): Miga[] {
  const ruta = url.split(/[?#]/)[0].replace(/^\/+|\/+$/g, '');
  const inicio: Miga = { texto: 'Inicio', url: '/home' };
  const lista = (segmentos: Miga[]): Miga[] => {
    const todas = [inicio, ...segmentos];
    if (extra) todas.push({ texto: extra, url: null });
    return todas.map((miga, i) => (i === todas.length - 1 ? { ...miga, url: null } : miga));
  };
  const etiqueta = (clave: string, defecto: string) => etiquetas[clave] ?? defecto;
  let m: RegExpMatchArray | null;

  if (ruta === '' || ruta === 'home') return [{ texto: 'Inicio', url: null }];
  if (ruta === 'asociaciones') return lista([{ texto: 'Asociaciones', url: '/asociaciones' }]);
  if ((m = ruta.match(/^asociaciones\/(\d+)$/))) {
    return lista([{ texto: 'Asociaciones', url: '/asociaciones' }, { texto: etiqueta('asociacion:' + m[1], 'Asociación ' + m[1]), url: '/asociaciones/' + m[1] }]);
  }
  if (ruta === 'administracion/asociaciones') return lista([{ texto: 'Asociaciones', url: '/asociaciones' }, { texto: 'Administración', url: '/administracion/asociaciones' }]);
  if (ruta === 'asociados') return lista([{ texto: 'Asociados', url: '/asociados' }]);
  if ((m = ruta.match(/^asociados?\/(\d+)\/historico$/))) {
    return lista([{ texto: 'Asociados', url: '/asociados' }, { texto: etiqueta('asociado:' + m[1], 'Asociado ' + m[1]), url: '/asociados/' + m[1] }, { texto: 'Histórico', url: ruta }]);
  }
  if ((m = ruta.match(/^asociados\/([^/]+)$/))) {
    return lista([{ texto: 'Asociados', url: '/asociados' }, { texto: etiqueta('asociado:' + m[1], /^\d+$/.test(m[1]) ? 'Asociado ' + m[1] : 'Asociado'), url: '/asociados/' + m[1] }]);
  }
  if (ruta === 'ejercicios') return lista([{ texto: 'Ejercicios', url: '/ejercicios' }]);
  if ((m = ruta.match(/^ejercicios\/(\d+)$/))) return lista([{ texto: 'Ejercicios', url: '/ejercicios' }, { texto: etiqueta('ejercicio:' + m[1], 'Ejercicio ' + m[1]), url: ruta }]);
  if (ruta === 'cargos') return lista([{ texto: 'Cargos', url: '/cargos' }]);
  if ((m = ruta.match(/^cargos\/(\d+)$/))) return lista([{ texto: 'Cargos', url: '/cargos' }, { texto: etiqueta('cargo:' + m[1], 'Cargo ' + m[1]), url: ruta }]);
  if (ruta === 'historico') return lista([{ texto: 'Histórico', url: '/historico' }]);
  if (ruta === 'historico/lote') return lista([{ texto: 'Histórico', url: '/historico' }, { texto: 'Asignar a varios ejercicios', url: null }]);
  if (ruta === 'permisos') return lista([{ texto: 'Permisos', url: '/permisos' }]);
  return [];
}

@Injectable({ providedIn: 'root' })
export class MigasService {
  private readonly url = signal('');
  private readonly etiquetas = signal<Record<string, string>>({});
  private readonly extra = signal<string | null>(null);

  readonly migas = computed(() => construirMigas(this.url(), this.etiquetas(), this.extra()));
  /** Migas con enlace: la penúltima es el «padre» al que lleva la versión compacta (móvil). */
  readonly padre = computed(() => {
    const lista = this.migas();
    return lista.length >= 2 ? lista[lista.length - 2] : null;
  });

  constructor(private readonly router: Router) {
    this.url.set(router.url);
    router.events.subscribe(evento => {
      if (evento instanceof NavigationEnd) {
        this.url.set(evento.urlAfterRedirects);
        this.extra.set(null); // un modo secundario («Crear», «Editar») no sobrevive a la navegación
      }
    });
  }

  /** Nombre de una entidad (p. ej. 'asociacion:12') para mostrarlo en la ruta. */
  etiqueta(clave: string, texto: string | null | undefined): void {
    const limpio = (texto ?? '').trim();
    if (!limpio || this.etiquetas()[clave] === limpio) return;
    this.etiquetas.update(actual => ({ ...actual, [clave]: limpio }));
  }

  /** Último tramo de la ruta cuando la pantalla está en un modo secundario («Crear asociación»); null lo quita. */
  fijarExtra(texto: string | null): void {
    this.extra.set(texto);
  }
}
